import {
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
  useEffect,
  useRef,
  useState,
} from 'react'
import { messageForError } from '../../api/errors'
import { Button } from '../ui/Button'
import { useFocusWhenEnabled } from '../ui/useFocusWhenEnabled'

/**
 * A setting is shown, not edited, until someone asks.
 *
 * Everything on the account page changes rarely — a name once, a password now
 * and then — so a text box at rest would invite typing where reading is the
 * job. This module is the shape every row shares: the row at rest, the inline
 * form that replaces it, and the state and focus choreography between them.
 */

// --- The row at rest ----------------------------------------------------------

interface SettingRowProps {
  label: string
  /** Beside the label, such as an InfoTip that explains it. */
  info?: ReactNode
  value: ReactNode
  /** A line beneath the value, such as where an email can be changed. */
  helper?: ReactNode
  /** Something the parent should know about the value, such as a card about to expire. */
  warning?: ReactNode
  /** Why the last change to this row did not take, for rows that save in place. */
  error?: string | null
  /** What can be done to it: an Edit button, a one-click change. */
  action?: ReactNode
  /**
   * Lets the row take focus itself, for a row that appears in place of one
   * whose control was just used and has no control of its own to land on.
   */
  ref?: Ref<HTMLDivElement>
  testId?: string
}

export function SettingRow({
  label,
  info,
  value,
  helper,
  warning,
  error,
  action,
  ref,
  testId,
}: SettingRowProps) {
  return (
    <div
      ref={ref}
      tabIndex={ref ? -1 : undefined}
      className={`py-3${ref ? ' rounded-field outline-none focus-visible:ring-2 focus-visible:ring-brand-ring' : ''}`}
      data-testid={testId}
    >
      {/* Only the label and value share a line with the action; anything
          longer runs the full width beneath, so it never wraps against a button. */}
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          {/* Positioned, so an info tip's note starts at the label's edge. */}
          <div className="relative flex items-center gap-1">
            <p className="text-xs font-semibold tracking-wider text-ink-soft uppercase">{label}</p>
            {info}
          </div>
          <p className="mt-0.5 text-base text-ink" data-testid="setting-value">
            {value}
          </p>
        </div>
        {action && <div className="flex shrink-0 items-center gap-3">{action}</div>}
      </div>
      {helper && <p className="mt-1 text-sm text-ink-soft">{helper}</p>}
      {warning && (
        <p className="mt-1 text-sm text-red-700" data-testid="setting-warning">
          {warning}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-1 text-sm text-red-700" data-testid="setting-error">
          {error}
        </p>
      )}
    </div>
  )
}

/** "Saved", beside a row's action, until the row is changed again. */
export function SavedIndicator({ children = 'Saved' }: { children?: ReactNode }) {
  return (
    <span role="status" className="text-sm text-green-700" data-testid="setting-saved">
      {children}
    </span>
  )
}

/** A value still loading, at the width of a short one so the row does not jump when it arrives. */
export function ValueSkeleton() {
  return (
    <>
      <span className="sr-only">Loading…</span>
      <span
        aria-hidden="true"
        className="inline-block h-4 w-32 rounded bg-field align-middle motion-safe:animate-pulse"
      />
    </>
  )
}

// --- The editor's state -------------------------------------------------------

interface SettingEditorOptions {
  /** Persist the draft. Rejections become the inline error. */
  save(): Promise<unknown>
  /** A reason not to save yet, or null. Runs before any request. */
  validate?(): string | null
  /**
   * Runs after a failed save, before the form is enabled again. Returns the
   * message for a failure the row understands better than the generic
   * mapping — clearing what needs clearing on the way — or null to leave it
   * to `messageForError`.
   */
  onError?(cause: unknown): string | null
}

const CONTROLS = 'input, select, textarea, button'

/** The first thing in a form that can take focus: its first field, or the submit of a form with none. */
function firstControl(form: HTMLFormElement | null): HTMLElement | null {
  return form?.querySelector<HTMLElement>(CONTROLS) ?? null
}

/**
 * Open, saving, saved and error state for one setting, plus the focus moves
 * that make an inline editor usable from the keyboard: into the form's first
 * control when it opens, back to the Edit control when it closes, and back to
 * the first control when a save fails, once the form is enabled again. "Saved"
 * stays until the row is edited again, which needs no timer and never
 * disappears mid-glance.
 */
export function useSettingEditor({ save, validate, onError }: SettingEditorOptions) {
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const editButton = useRef<HTMLButtonElement>(null)
  const form = useRef<HTMLFormElement>(null)
  // Only a close that followed an open returns focus; the initial render must
  // not steal it from wherever the page had put it.
  const returnFocus = useRef(false)
  // A failed save cannot focus a field straight away: the fieldset stays
  // disabled until `saving` clears. Asking records the wish for then.
  const focusFirstControl = useFocusWhenEnabled(saving, () => firstControl(form.current))

  useEffect(() => {
    if (open) {
      firstControl(form.current)?.focus()
    } else if (returnFocus.current) {
      returnFocus.current = false
      editButton.current?.focus()
    }
  }, [open])

  function start() {
    setSaved(false)
    setError(null)
    setOpen(true)
  }

  function cancel() {
    returnFocus.current = true
    setOpen(false)
  }

  async function submit() {
    const reason = validate?.()
    if (reason) {
      setError(reason)
      firstControl(form.current)?.focus()
      return
    }

    setSaving(true)
    setError(null)
    try {
      await save()
      setSaved(true)
      cancel()
    } catch (cause) {
      setError(onError?.(cause) ?? messageForError(cause))
      focusFirstControl()
    } finally {
      setSaving(false)
    }
  }

  return { open, saving, saved, error, start, cancel, submit, editButton, form }
}

export type SettingEditor = ReturnType<typeof useSettingEditor>

// --- The control that opens the editor ---------------------------------------

interface SettingEditButtonProps {
  editor: SettingEditor
  /** Opens the editor, after the caller has reset its draft. */
  onClick(): void
  /** The accessible name: "Edit name" reads better than a bare "Edit". */
  'aria-label': string
  /** What the row reports once a save lands; "Saved" unless the action has a better word. */
  savedLabel?: string
  children: string
}

export function SettingEditButton({
  editor,
  onClick,
  savedLabel,
  children,
  ...aria
}: SettingEditButtonProps) {
  return (
    <>
      {editor.saved && <SavedIndicator>{savedLabel}</SavedIndicator>}
      <Button
        ref={editor.editButton}
        variant="secondary"
        onClick={onClick}
        data-testid="setting-edit"
        {...aria}
      >
        {children}
      </Button>
    </>
  )
}

// --- The inline form ----------------------------------------------------------

interface SettingFormProps {
  editor: SettingEditor
  submitLabel: string
  /** What the way out is called; "Cancel" unless that word means something else in the form. */
  cancelLabel?: string
  /** Something to say beneath the fields, such as where to write a password down. */
  note?: ReactNode
  children: ReactNode
}

/** Enter saves, Escape cancels, and nothing can be typed into while a save is in flight. */
export function SettingForm({
  editor,
  submitLabel,
  cancelLabel = 'Cancel',
  note,
  children,
}: SettingFormProps) {
  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    void editor.submit()
  }

  function onKeyDown(event: KeyboardEvent<HTMLFormElement>) {
    if (event.key !== 'Escape') return
    event.preventDefault()
    editor.cancel()
  }

  return (
    <form ref={editor.form} onSubmit={onSubmit} onKeyDown={onKeyDown} noValidate className="py-3">
      <fieldset disabled={editor.saving} className="flex flex-col gap-3">
        {children}
        {note && <p className="text-sm text-ink-soft">{note}</p>}
        {editor.error && (
          <p role="alert" className="text-sm text-red-700" data-testid="setting-error">
            {editor.error}
          </p>
        )}
        <div className="flex items-center gap-3">
          <Button type="submit" variant="compact" data-testid="setting-save">
            {editor.saving ? 'Saving…' : submitLabel}
          </Button>
          <Button variant="secondary" onClick={editor.cancel} data-testid="setting-cancel">
            {cancelLabel}
          </Button>
        </div>
      </fieldset>
    </form>
  )
}

/** A validator for the common case: the field may not be left empty. */
export function required(message: string) {
  return (value: string) => (value === '' ? message : null)
}
